import React from 'react';
import PropTypes from 'prop-types';

import {
  LITTER_STATES,
  LITTER_STATE_TITLES
} from 'dpl/shared/constants/shared';
import { BreederProfileLitterPropTypes } from 'dpl/shared/utils/customPropTypes';
import Icon from 'dpl/common/components/Icon';
import { getLitterModalUrl } from 'dpl/dog_modals/utils';

import { BreederProfileLitterIcon } from '../BreederProfileLitterIcon';

const { BORN } = LITTER_STATES;
const MAX_LITTERS_TO_SHOW = 3;

export function BreederProfileLittersSectionListHeaderCell({ children }) {
  return (
    <td className="BreederProfileLittersSectionListHeaderCell v-mid">
      <div className="pb2 black-50 f2 fw-medium">{children}</div>
    </td>
  );
}

BreederProfileLittersSectionListHeaderCell.propTypes = {
  children: PropTypes.node
};

BreederProfileLittersSectionListHeaderCell.defaultProps = {
  children: null
};

export function BreederProfileLittersSectionListBodyCell({ children }) {
  return (
    <td className="BreederProfileLittersSectionListBodyCell v-mid">
      <div className="pv2">{children}</div>
    </td>
  );
}

BreederProfileLittersSectionListBodyCell.propTypes = {
  children: PropTypes.node.isRequired
};

export function BreederProfileLittersSectionListItem({ litter, onClick }) {
  const {
    birth_date_month_year: birthDate,
    ready_date_month_year: readyDate,
    status
  } = litter;

  const displayBirthDate = `${status === BORN ? 'Born' : 'Due'} ${birthDate}`;

  return (
    <tr
      className="BreederProfileLitterSectionListItem transition hover-bg-near-white pointer"
      data-test-id={`litter-${litter.id}`}
      onClick={onClick}
      href={getLitterModalUrl(litter.id)}
    >
      <BreederProfileLittersSectionListBodyCell>
        <BreederProfileLitterIcon status={litter.status} />
      </BreederProfileLittersSectionListBodyCell>
      <BreederProfileLittersSectionListBodyCell>
        {LITTER_STATE_TITLES[status]} Puppies
      </BreederProfileLittersSectionListBodyCell>
      <BreederProfileLittersSectionListBodyCell>
        {litter.breed_name}
      </BreederProfileLittersSectionListBodyCell>
      <BreederProfileLittersSectionListBodyCell>
        {displayBirthDate}
      </BreederProfileLittersSectionListBodyCell>
      <BreederProfileLittersSectionListBodyCell>
        {readyDate}
      </BreederProfileLittersSectionListBodyCell>
      <BreederProfileLittersSectionListBodyCell>
        <Icon
          className="pv3 ph3 bg-near-white purple br-100"
          height="44px"
          name="expand"
          width="44px"
        />
      </BreederProfileLittersSectionListBodyCell>
    </tr>
  );
}

BreederProfileLittersSectionListItem.propTypes = {
  litter: PropTypes.shape(BreederProfileLitterPropTypes).isRequired,
  onClick: PropTypes.func.isRequired
};

export default function BreederProfileLittersSection({
  isExpanded,
  litters,
  onLitterClick,
  toggle
}) {
  const hasMoreLitters = litters.length > MAX_LITTERS_TO_SHOW;
  const visibleLitters = litters.slice(
    0,
    isExpanded ? litters.length : MAX_LITTERS_TO_SHOW
  );

  return (
    <section className="BreederProfileLittersSection">
      <h5 className="mb6 f5">Newborn & upcoming litters</h5>
      <table className="w-100">
        <thead>
          <tr className="mb1 bb bw2 b--near-white">
            <BreederProfileLittersSectionListHeaderCell />
            <BreederProfileLittersSectionListHeaderCell>
              <p>Status</p>
            </BreederProfileLittersSectionListHeaderCell>
            <BreederProfileLittersSectionListHeaderCell>
              <p>Breed</p>
            </BreederProfileLittersSectionListHeaderCell>
            <BreederProfileLittersSectionListHeaderCell>
              <p>Birth/Due Date</p>
            </BreederProfileLittersSectionListHeaderCell>
            <BreederProfileLittersSectionListHeaderCell>
              <p>Ready</p>
            </BreederProfileLittersSectionListHeaderCell>
            <BreederProfileLittersSectionListHeaderCell />
          </tr>
        </thead>
        <tbody>
          {visibleLitters.map(litter => (
            <BreederProfileLittersSectionListItem
              key={litter.id}
              litter={litter}
              onClick={e => {
                e.preventDefault();
                onLitterClick(litter.id);
              }}
            />
          ))}
        </tbody>
      </table>
      {hasMoreLitters && (
        <button onClick={toggle} className="link--primary mt6" type="button">
          {isExpanded ? 'Show Less -' : 'Show More +'}
        </button>
      )}
    </section>
  );
}

BreederProfileLittersSection.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  litters: PropTypes.arrayOf(PropTypes.shape(BreederProfileLitterPropTypes))
    .isRequired,
  onLitterClick: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired
};
